/**
 * This module applies highchart modules and custom highcharts plugins used by Seeq
 *
 * @exports configured Highcharts object
 */
import 'highcharts/es-modules/masters/highcharts-more.src.js';
import 'highcharts/es-modules/masters/modules/broken-axis.src.js';
import 'highcharts/es-modules/masters/modules/heatmap.src.js';
import 'highcharts/es-modules/masters/modules/solid-gauge.src.js';
import 'highcharts/es-modules/masters/modules/accessibility.src.js';
import { FLAME_SERIES_OPTIONS, Highcharts, seriesTypes } from './highcharts.constants';
import {
  configureNegativeLogAxis,
  createBeforeRedrawHandler,
  createFlameAlignDataLabel,
  createMinPixelSpacingGraphPath,
  getBrokenAxisLinePath,
  refreshTooltipWithSeriesBackground,
  shimHighchartsGetContainer,
  suppressDisplayError12,
} from './highcharts.utilities';
import type { HighchartsAxisWithLogarithmic, HighchartsChartWithRendererUrl } from './highcharts.types';

// ============================================================================
// IMPORTANT: Highcharts Extension Best Practices
// ============================================================================
// When extending Highcharts, use the appropriate method for your use case:
//
// ✅ Creating Custom Series: Use registerSeriesType()
// ✅ Event Hooks:          Use addEvent()
// ⚠️  Global Modifications: Use wrap() ONLY for Axis/Tooltip/Chart prototypes
// ❌ NEVER:                 wrap() on series rendering methods (getGraphPath, etc.)
//
// ----------------------------------------------------------------------------
// This custom series type uses seriesType() to create an ISOLATED prototype.
// DO NOT use wrap() on series methods - it causes prototype pollution.
// ----------------------------------------------------------------------------
//
// Why Custom Series Type > wrap():
//   1. ISOLATION: Only affects series explicitly using the custom type
//   2. NO SIDE EFFECTS: Base 'line' prototype remains untouched
//   3. CLEAR INTENT: Type declaration makes custom behavior explicit
//
// Real Bug (CRAB-57105): Original code used wrap(line.prototype, 'getGraphPath')
//   • Even with guard clause, wrap() changed function identity
//   • Area/arearange series internally call line.getGraphPath
//   • Broke discontinuous threshold rendering in scorecards
//   • Fixed by using seriesType() to isolate behavior
//
// Decision Guide:
//   Custom series rendering?     → seriesType() or registerSeriesType()
//   Chart lifecycle hooks?       → addEvent()
//   Modify Axis/Tooltip/Chart?   → wrap() is acceptable
//   Modify series.getGraphPath?  → NEVER wrap(), use custom type instead
//
// Note: seriesType() is deprecated but functional. Modern code should use
// registerSeriesType() with ES6 classes.
//
// References: CRAB-57105, CRAB-56445
// ============================================================================

/**
 * Extend the Axis.getLinePath method in order to visualize breaks with two parallel
 * slanted lines. For each break, the slanted lines are inserted into the line path.
 */
Highcharts.wrap(Highcharts.Axis.prototype, 'getLinePath', getBrokenAxisLinePath);

/**
 * Enforce minimum 1-pixel spacing between consecutive points in line series.
 * This prevents invisible line segments when data points are very close together.
 * Applies to the custom `lineWithMinPixelSpacing` line series type.
 */
Highcharts.seriesType(
  'lineWithMinPixelSpacing',
  'line',
  {},
  {
    getGraphPath: createMinPixelSpacingGraphPath,
  },
);

Highcharts.wrap(Highcharts.Tooltip.prototype, 'refresh', refreshTooltipWithSeriesBackground);

/**
 * Allows negative values for logarithmic Y axes. Taken from:
 * https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/yaxis/type-log-negative/
 */
Highcharts.addEvent(Highcharts.Axis as unknown as HighchartsAxisWithLogarithmic, 'afterInit', configureNegativeLogAxis);

Highcharts.addEvent(Highcharts.Chart, 'displayError', suppressDisplayError12);

Highcharts.addEvent(
  Highcharts.Chart as unknown as HighchartsChartWithRendererUrl,
  'beforeRedraw',
  createBeforeRedrawHandler,
);

Highcharts.seriesType('flame', 'columnrange', FLAME_SERIES_OPTIONS, {
  drawDataLabels: seriesTypes.line.prototype.drawDataLabels,
  alignDataLabel: createFlameAlignDataLabel(seriesTypes.column.prototype.alignDataLabel),
});

Highcharts.wrap(Highcharts.Chart.prototype, 'getContainer', shimHighchartsGetContainer);

export default Highcharts;
